/**
 * What the Validate panel is entitled to claim.
 *
 * It used to derive "ok" like this:
 *
 *     if (saveStatus === "saved" && documentValidation?.valid !== false)
 *         return "ok";
 *
 * Two things wrong with that, and both were visible in the wild on
 * 2026-09-08: a document reporting "Document validates with no errors or
 * warnings" while pdflatex died on it with an undefined control sequence.
 *
 *  1. `?.valid !== false` is TRUE when there is no result at all. A document
 *     that had never been validated, but had been saved, reported a clean
 *     bill of health. The panel was reading "saved" as "valid" — and saving
 *     says nothing about whether the thing compiles.
 *
 *  2. Nothing invalidated a result when the document changed. A pass from
 *     before an edit kept showing green afterwards, which is the same lie
 *     told more slowly.
 *
 * So: only a real, current pass earns "ok". Everything else says what it
 * actually knows — nothing yet, or not since you changed it.
 */

export type ValidationStatus = "error" | "warning" | "stale" | "ok" | "idle";

export interface ValidationSnapshot {
  valid: boolean;
  error?: string;
  warnings?: string[];
}

export function validationStatus(
  result: ValidationSnapshot | null | undefined,
  stale = false,
): ValidationStatus {
  // Never run. "Not validated yet" is the honest answer, and saveStatus does
  // not get a vote — it never should have.
  if (!result) return "idle";

  // A failure stays a failure even once the document has moved on: it is the
  // last thing we know, and it was bad. Reporting "stale" would soften it.
  if (result.valid === false) return "error";

  // A pass, on the other hand, only speaks for the document it ran against.
  if (stale) return "stale";

  if ((result.warnings?.length ?? 0) > 0) return "warning";
  return "ok";
}

/**
 * What a validation pass ran against.
 *
 * Only the fields that reach the compiler — id, type and content. A block
 * whose `updatedAt` moved but whose content did not would otherwise make a
 * perfectly good result look stale.
 */
export function validationSignature(
  blocks: readonly { id: string; type: string; content?: unknown }[],
): string {
  return JSON.stringify(blocks.map((b) => [b.id, b.type, b.content]));
}
